import { Box } from "@mui/material";
import { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import SetDefaultRecycledValuesButton from "../components/button/SetDefaultValuesButton";
import AgGrid from "../components/grid/AgGrid";
import { defaultColDef, floatFormater, leftAlignColDef, preciseFloatFormater, rightAlignColDef } from "../components/grid/columnDefinitions/columnDefinitions";
import { useContextProvider } from "../components/page-context/ContextProvider";
import { Circularity } from "../db_access/circularity";
import { calculate_om_landfill_waste, calculate_om_waste_content, recalculate_row_mci } from "../functions/CircularityFunctions";
import ColumnMenu from "../components/context-menu/ColumnMenu";

function OutputMaterialTable({ circularities, columnMenuVisible, setColumnMenuVisible, anchorRef, setCircularities }) {
  const { isSaveClicked, setIsSaveVisible, setRefresh, setIsLoading, setIsSaveClicked, envFactorOptions, columnVisibilities, enqueueAlert } = useContextProvider();
  const { t } = useTranslation();
  const [outputMaterials, setOutputMaterials] = useState([]);
  const [modified, setModified] = useState(false);
  const gridRef = useRef(null);

  const colVisibility_key = "circularity_output_material_table_visibility";

  const rawColDefs = [
    {
      headerName: "SI No.",
      valueGetter: "node.rowIndex + 1",
      width: 80,
      minWidth: 60,
      headerClass: "ag-center-aligned-header",
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      suppressMenu: true,
      sortable: false,
      field: "si_no"
    },
    { field: "description", headerName: "Description", ...leftAlignColDef, ...defaultColDef, flex: 1 },
    { field: "om_recycling", headerName: "Sent to Recycling (%)", ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: true },
    { field: "om_reuse", headerName: "Reused (%)", ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: true },
    { field: "om_bio_composted", headerName: "Biological materials (Composted) (%)", ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: true },
    { field: "om_bio_energy_recovery", headerName: "Biological materials (Energy Recovery) (%)", ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: true },
    { field: "om_waste_content", headerName: "Waste Content (%)", ...rightAlignColDef, ...defaultColDef, ...floatFormater },
    { field: "om_landfill_waste", headerName: "Waste to landfill (% of total mass)", ...rightAlignColDef, ...defaultColDef, ...floatFormater },


  ];


  // Hide Biogenic columns by default
  const defaultCols = useMemo(() => rawColDefs
    .filter(c => !["om_bio_composted", "om_bio_energy_recovery"].includes(c.field))
    .map((c) => c.field), 
  []);

  const columnDefs = useMemo(() => {
    const isHidden = (field) => {
      if (field === "si_no") return false;
      return !columnVisibilities[colVisibility_key]?.includes(field);
    };
    return rawColDefs.map((col) => ({
      ...col,
      hide: isHidden(col.field),
    }));
  }, [columnVisibilities, colVisibility_key]);

  // Load initial data and fetch recycling data
  useEffect(() => {
    if (modified || isSaveClicked) return; // Don't sync if we have local changes or are saving
    const updated = circularities.map(om => {
      const new_om = { ...om };
      // Apply default recycling if it's null or undefined
      if (new_om.om_recycling === null || new_om.om_recycling === undefined) {
        new_om.om_recycling = (envFactorOptions?.[om.type]?.[om.category]?.[om.sub_category]?.[om.description]?.[0]?.["recyclability"] || 0) * 100;
      }

      
      if (new_om.om_recycling !== om.om_recycling) {
        new_om.om_waste_content = calculate_om_waste_content(new_om);
        new_om.om_landfill_waste = calculate_om_landfill_waste(new_om);
        // Removed setModified(true) to prevent loop
      }
      return new_om;
    });
    setOutputMaterials(updated);
  }, [circularities, envFactorOptions, modified, isSaveClicked]);




  useEffect(() => {
    if (isSaveClicked && modified) {
      const payload = outputMaterials.map((item) => ({ ...item, id: Number(item.id) }));
      Circularity.update_output_materials(payload).finally(() => {
        setModified(false);
        // Parent handles setIsLoading and setIsSaveClicked after the 2s MCI sync
      });

    }
  }, [isSaveClicked]);

  useEffect(() => {
    if (modified) {
      setIsSaveVisible(true);
    }
  }, [modified]);

  const setDefaultRecycledValues = () => {
    const updatedData = outputMaterials.map((om) => {
      const new_om = { ...om };
      new_om.om_recycling = (envFactorOptions?.[om.type]?.[om.category]?.[om.sub_category]?.[om.description]?.[0]?.["recyclability"] || 0) * 100;
      new_om.om_waste_content = calculate_om_waste_content(new_om);
      new_om.om_landfill_waste = calculate_om_landfill_waste(new_om);
      return new_om;
    });
    setOutputMaterials(updatedData);
    setModified(true);
  };

  const onCellValueChanged = (params) => {
    const { data, colDef, value, oldValue } = params;
    let validatedValue = value;
    if (["om_recycling", "om_reuse", "om_bio_composted", "om_bio_energy_recovery"].includes(colDef.field)) {
      const parsed = parseFloat(value);
      if (isNaN(parsed) || parsed < 0 || parsed > 100) {
        if (enqueueAlert) {
          enqueueAlert({
            message: t("Value must be between 0 and 100."),
            severity: "warning",
          });
        }
        params.node.setDataValue(colDef.field, oldValue !== undefined ? oldValue : 0);
        return;
      }

      const temp_om = { ...data, [colDef.field]: parsed };
      const or = parseFloat(temp_om.om_recycling) || 0;
      const ou = parseFloat(temp_om.om_reuse) || 0;
      const obc = parseFloat(temp_om.om_bio_composted) || 0;
      const obe = parseFloat(temp_om.om_bio_energy_recovery) || 0;
      if (or + ou + obc + obe > 100) {
        if (enqueueAlert) {
          enqueueAlert({
            message: t("The sum of Sent to Recycling, Reused, Biological materials (Composted), and Biological materials (Energy Recovery) cannot exceed 100%."),
            severity: "warning",
          });
        }
        params.node.setDataValue(colDef.field, oldValue !== undefined ? oldValue : 0);
        return;
      }
      validatedValue = parsed;
    }
    const updated = outputMaterials.map((om) => {
      if (om.id === data.id) {
        const new_om = { ...om, [colDef.field]: validatedValue };
        return recalculate_row_mci(new_om);
      }
      return om;
    });
    setOutputMaterials(updated);
    setModified(true);
    // Notify parent for live summary updates
    if (setCircularities) {
      setCircularities(updated);
    }
  };


  return (
    <Box>
      <Box sx={{ height: "auto" }}>
        <AgGrid 
          ref={gridRef}
          columnDefs={columnDefs} 
          rowData={outputMaterials} 
          onCellValueChanged={onCellValueChanged} 
          headerCheckbox={false} 
        />
      </Box>
      <ColumnMenu
        anchorRef={anchorRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={rawColDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
        gridRef={gridRef}
      />
    </Box>

  );
}

export default OutputMaterialTable;

